import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';

import { updateSearch } from 'bernie-core';

import { ILocalization } from '@shared-ui/localization-context';
import { SearchFormInputs } from '@shared-ui/search-tools-lodging';

import { evaluateAndLogExperiment } from 'stores/utils';
import { SearchResultsStore } from 'src/common/stores/search-results-store';

import { HOUSE_RULES_GROUP } from 'src/common/constants/filters';
import { MarketingChannels, MarketingChannelsTypeCodes } from 'constants/marketing-channels';

import { getAdultsAndChildrenFromRooms } from 'src/common/utils/room-utils';

import { DatePeriodInput, PropertyDateRangeInput, PropertyFilterAmenity, SelectedValueInput } from 'src/common/__generated__/api';
import { History, Location } from 'history';

interface UpdatedSearchParams {
  destination: string;
  regionId: string;
  startDate: string;
  endDate: string;
  adults: string;
  children: string;
  amenities?: string;
  // eslint-disable-next-line camelcase
  total_price?: string;
  pwaDialog?: string;
  selected?: string;
  // eslint-disable-next-line camelcase
  house_rules_group?: HOUSE_RULES_GROUP[];
  neighborhood?: string;
  // eslint-disable-next-line camelcase
  neighborhood_group?: string;
}

export const updateResultsWithNewCriteria = (
  { propertySearchCriteriaInput: criteria }: SearchFormInputs,
  location: Location,
  history: History,
  searchResults: SearchResultsStore,
  experiment: ExperimentStore,
  l10n: ILocalization
) => {
  searchResults.clearDefaultRequestFields();
  searchResults.shouldFetch = true;

  const { primary, secondary } = criteria || {};
  const { regionName: destination, regionId, coordinates, pinnedPropertyId } = primary?.destination || {};
  const dateStrings = primary?.dateRange ? getDateStringsFromDateRangeInput(primary.dateRange, l10n) : undefined;
  const startDate = dateStrings?.startDate;
  const endDate = dateStrings?.endDate;
  const adultsAndChidlren = primary?.rooms ? getAdultsAndChildrenFromRooms(primary.rooms) : undefined;
  const adults = adultsAndChidlren?.adults;
  const children = adultsAndChidlren?.children;
  const latLong = coordinates ? [coordinates.latitude, coordinates.longitude].join(',') : '';
  const params = new URLSearchParams(location.search);
  const searchParamStartDate = params.get('startDate');
  const searchParamEndDate = params.get('endDate');
  const searchParamRegionId = params.get('regionId');
  const flexibility = primary?.flexSearchCriteria?.flexibility ? getDateFlexibility(primary.flexSearchCriteria.flexibility) : undefined;

  const newParams: any = {
    destination,
    latLong,
    regionId,
    startDate,
    endDate,
    adults,
    children,
    pwaDialog: null,
    selected: pinnedPropertyId,
    mapBounds: '',
    flexibility,
    ...(searchResults?.searchCriteria?.dateSelectedByUser && { dateSelectedByUser: searchResults.searchCriteria.dateSelectedByUser }),
  };

  if (pinnedPropertyId) {
    newParams.regionId = null;
  }

  if (startDate !== searchParamStartDate || endDate !== searchParamEndDate) {
    // setting the total_price property of the newParams object explicitly to undefined so that updateSearch()
    // removes the total_price url parameter. nightly_price should remain unaffected by date changes.

    // eslint-disable-next-line camelcase
    (newParams as unknown as UpdatedSearchParams).total_price = undefined;
  }

  if (regionId !== searchParamRegionId) {
    (newParams as unknown as UpdatedSearchParams).neighborhood = undefined;
    // eslint-disable-next-line camelcase
    (newParams as unknown as UpdatedSearchParams).neighborhood_group = undefined;
  }

  if (experiment?.exposures?.Vrbex_generic?.bucket === 1) {
    (newParams as unknown as UpdatedSearchParams).amenities = getAmenities(searchResults, secondary?.selections);
    // eslint-disable-next-line camelcase
    (newParams as unknown as UpdatedSearchParams).house_rules_group = getHouseRules(searchResults, secondary?.selections);
  }

  /*
    HACK: sadly this setTimeout has to be reinstated after it was recently removed. Without this setTimeout, when the search
    form's traveler selector input is updated and submitted on mWeb an incorrect search is fired off (ie. the prior number
    oftravelers searched form). The updateSearch() call made below runs and completes first, properly updating the adults/
    children url params with new values as selected. However, since this bug affects mWeb, the traveler search form input is
    opened as a dialog and when it is submitted, bernie also calls updateSearch() to remove the pwaDialog url parameters added
    by opening the traveler selector input as a dialog. When this bernie updateSearch() call occurs, for some reason the
    location it is passed is stale (or the prior traveler input values) - cause the properly updated url to be reverted to the
    state it was prior to the traveler selctor dialog opening. A new search is then triggered, but with incorrect values for
    the number of travelers.
  */
  setTimeout(() => updateSearch({ location, history, newParams, replace: false }), 25);
};

export const getHouseRules = (searchResults: SearchResultsStore, selections?: SelectedValueInput[] | null): HOUSE_RULES_GROUP[] => {
  let rules = searchResults.searchCriteria.house_rules_group;
  if (selections) {
    rules = rules.filter(rule => rule !== HOUSE_RULES_GROUP.PETS_ALLOWED);
    const selectionsHasPets = selections.some(sel => sel.value === HOUSE_RULES_GROUP.PETS_ALLOWED);
    if (selectionsHasPets) {
      rules.push(HOUSE_RULES_GROUP.PETS_ALLOWED);
    }
  }

  return rules;
};

export const getAmenities = (searchResults: SearchResultsStore, selections?: SelectedValueInput[] | null): string => {
  let amenities = searchResults.searchCriteria.amenities;

  if (selections) {
    amenities = amenities.filter(amenity => amenity !== PropertyFilterAmenity.PETS);
    const selectionsHasPets = selections.some(sel => sel.value === HOUSE_RULES_GROUP.PETS_ALLOWED);
    if (selectionsHasPets) {
      amenities.push(PropertyFilterAmenity.PETS);
    }
  }

  return amenities.filter(Boolean).join();
};

export const getDateStringsFromDateRangeInput = (dateRange: PropertyDateRangeInput, l10n: ILocalization) => {
  if (dateRange === null) {
    return { startDate: undefined, endDate: undefined };
  }

  const { checkInDate, checkOutDate } = dateRange;
  const startDateObj = new Date(checkInDate.year, checkInDate.month - 1, checkInDate.day);
  const endDateObj = new Date(checkOutDate.year, checkOutDate.month - 1, checkOutDate.day);

  return {
    startDate: l10n.formatDate(startDateObj, { raw: 'yyyy-MM-dd' }),
    endDate: l10n.formatDate(endDateObj, { raw: 'yyyy-MM-dd' }),
  };
};

export const getDateFlexibility = (flexibility: DatePeriodInput[]) =>
  flexibility.map(({ length, unit, bound }) => {
    const params = bound ? [length, unit, bound] : [length, unit];

    return params.join('_');
  });

export const isVrbexExperiment = (experiment: ExperimentStore) => experiment?.exposures?.Vrbex_generic?.bucket === 1;

export const isSRPCalendarAutoOpenExperiment = (experiment: ExperimentStore, context: ContextStore) =>
  Boolean(evaluateAndLogExperiment('Landing_SRP_Calendar_opening_by_default', context, experiment));

export const isMetaChannelDefaultDatesSearch = (searchQueryParams: URLSearchParams) => {
  const marketingChannelTypeCode = searchQueryParams.get('mctc');
  switch (marketingChannelTypeCode) {
    case MarketingChannelsTypeCodes.TRIVAGO:
      return Boolean(searchQueryParams.get(MarketingChannels.mdpdtl)?.includes('defdate=1'));
    case MarketingChannelsTypeCodes.TRIPADVISOR:
      return (
        Boolean(searchQueryParams.get(MarketingChannels.icmdtl)?.includes('defdate.1')) ||
        Boolean(searchQueryParams.get(MarketingChannels.icmdtl.toUpperCase())?.includes('defdate.1'))
      );
    case MarketingChannelsTypeCodes.GOOGLE_HA:
      return Boolean(searchQueryParams.get(MarketingChannels.mdpdtl.toUpperCase())?.includes('DDT'));
    default:
      return false;
  }
};

export const isSEMChannelDefaultDatesSearch = (searchQueryParams: URLSearchParams) =>
  searchQueryParams.get(MarketingChannels.semcid) !== null;

export const isShowWeekdayExperiment = (experiment: ExperimentStore, context: ContextStore) =>
  evaluateAndLogExperiment('Day_of_week_in_Lodging_Search_Form', context, experiment) === 1;

export const isAlternateDestinationFeatureGate = (experiment: ExperimentStore, context: ContextStore) =>
  evaluateAndLogExperiment('Alternate_Destination_Recs_SRP_Lodging_Feature_Gate', context, experiment) === 1;
